import React, { useState, useEffect, useMemo } from "react";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import { Row, Col, Form, Button, Offcanvas } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useAppContext } from "../context/AppContext";
import {
  DynamicDetailedProductCard,
  normalizeImage,
  getItemKey,
} from "./components/ProductCard";
import SeoHelmet from "./components/SeoHelmet";
import FloatingWidgets from "./components/FloatingWidgets";

const slugify = (value = "") =>
  String(value)
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const parseWeightToGrams = (weight) => {
  const value = String(weight || "")
    .toLowerCase()
    .trim();
  if (value.endsWith("kg"))
    return Number(value.replace("kg", "").trim()) * 1000;
  if (value.endsWith("g")) return Number(value.replace("g", "").trim());
  return 0;
};

const formatWeight = (grams) =>
  grams >= 1000 ? `${grams / 1000}kg` : `${grams}g`;

const prepareProduct = (product, categoryMap) => {
  const categoryId =
    typeof product.category === "object"
      ? product.category._id
      : product.category;
  const categoryName =
    categoryMap[String(categoryId)]?.name ||
    product.category?.name ||
    "Products";


  const basePrice = Number(product.price || 0);
  const baseMrp = Number(product.mrp || 0);

  let availableWeightOptions = [];
  let isSoldOut = true;

  if (product.variants && product.variants.length > 0) {
    availableWeightOptions = product.variants
      .filter((variant) => variant.stock > 0)
      .map((variant) => ({
        weight: variant.weight,
        mrp: variant.mrp,
        price: variant.price,
        stock: variant.stock,
      }));

    if (availableWeightOptions.length > 0) {
      isSoldOut = false;
    }
  } else {
    // Fallback for products without variants or if variants are empty
    const baseWeightGrams = parseWeightToGrams(
      product.weight || product.netwt || "500g",
    );

    if (baseWeightGrams > 0) {
      const optionsInGrams = [250, 500, 1000].filter(
        (g) => g <= baseWeightGrams || g === 500,
      );
      availableWeightOptions = optionsInGrams.map((grams) => {
        const ratio = grams / (baseWeightGrams || 500);
        return {
          weight: formatWeight(grams),
          price: Math.round(basePrice * ratio),
          mrp: Math.round(baseMrp * ratio),
          stock: product.stock || 1, // Assume stock from top-level if no variants
        };
      });
    }
    if (product.stock > 0) {
      isSoldOut = false;
    }
  }

  const defaultOption =
    availableWeightOptions.find((o) => o.weight === "250g") ||
    availableWeightOptions[0] ||
    null;

  return {
    ...product,
    weightOptions: availableWeightOptions,
    img: normalizeImage(product.mainImage || product.image || product.img),
    categoryName,
    price: defaultOption ? defaultOption.price : basePrice,
    old_price: defaultOption ? defaultOption.mrp : baseMrp,
    quantity: 1,
    net_wt: defaultOption ? defaultOption.weight : "500g",
    weights: availableWeightOptions.map((o) => o.weight),
    isSoldOut: isSoldOut,
  };
};

export default function Shopnow() {
  const { categorySlug, query } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useAppContext();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(2000);
  const [searchTerm, setSearchTerm] = useState("");
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/content/categories?status=Published`),
          fetch(`${API_BASE_URL}/api/content/products?status=Published`),
        ]);
        const catJson = await catRes.json();
        const prodJson = await prodRes.json();

        const catList = Array.isArray(catJson?.data) ? catJson.data : [];
        const prodList = Array.isArray(prodJson?.data) ? prodJson.data : [];

        const catMap = catList.reduce((acc, c) => {
          acc[String(c._id)] = c;
          return acc;
        }, {});

        setCategories(
          catList.filter((c) => !c.isDeleted && c.status === "Published"),
        );
        setProducts(
          prodList
            .filter((p) => !p.isDeleted)
            .map((p) => prepareProduct(p, catMap)),
        );
      } catch (err) {
        console.error("Error loading Shop Now data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Sync search term with URL path parameter "query"
  useEffect(() => {
    if (query) {
      setSearchTerm(query.replace(/-/g, " "));
    }
  }, [query]);

  // Sync selected category with URL parameter
  useEffect(() => {
    if (!loading && categories.length > 0) {
      if (categorySlug) {
        const matched = categories.find(
          (c) => c.slug === categorySlug || slugify(c.name) === categorySlug,
        );
        if (matched) setSelectedCategory(matched.name);
        else setSelectedCategory("All");
      } else {
        setSelectedCategory("All");
      }
    }
  }, [categorySlug, categories, loading]);

  // Reset visibleCount when any filter changes
  useEffect(() => {
    setVisibleCount(10);
  }, [selectedCategory, minPrice, maxPrice, searchTerm]);

  const filteredProducts = useMemo(() => {
    let result = products.filter((p) => {
      const matchCat =
        selectedCategory === "All" || p.categoryName === selectedCategory;
      const isNotGiftBox = p.categoryName !== "Gift Boxes";
      const matchPrice = p.price >= minPrice && p.price <= maxPrice;
      const matchSearch = p.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchCat && isNotGiftBox && matchPrice && matchSearch;
    });

    return result.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [products, selectedCategory, minPrice, maxPrice, searchTerm]);

  const displayedProducts = useMemo(() => {
    if (!isMobile) return filteredProducts;
    return filteredProducts.slice(0, visibleCount);
  }, [filteredProducts, visibleCount, isMobile]);

  // Show only categories that actually contain products (and aren't hard-excluded like Gift Boxes)
  const categoriesWithProducts = useMemo(() => {
    return categories.filter(
      (cat) =>
        cat.name !== "Gift Boxes" &&
        products.some((p) => p.categoryName === cat.name),
    );
  }, [categories, products]);

  const handleResetFilters = () => {
    navigate("/categories");
    setMinPrice(0);
    setMaxPrice(2000);
    setSearchTerm("");
  };

  const handleQuantityChange = (itemKey, delta) => {
    setProducts((prev) =>
      prev.map((p) =>
        getItemKey(p) === itemKey
          ? { ...p, quantity: Math.max(1, p.quantity + delta) }
          : p,
      ),
    );
  };

  const handleWeightChange = (itemKey, newWeight) => {
    setProducts((prev) =>
      prev.map((p) => {
        if (getItemKey(p) === itemKey) {
          const opt = p.weightOptions.find((o) => o.weight === newWeight);
          return {
            ...p,
            net_wt: newWeight,
            price: opt.price,
            old_price: opt.mrp,
          };
        }
        return p;
      }),
    );
  };

  const handleAddToCart = (product, redirect = false) => {
    const item = {
      productId: product._id || product.slug,
      name: product.name,
      img: product.img,
      price: product.price,
      quantity: product.quantity,
      net_wt: product.net_wt,
      categoryName: product.categoryName,
    };
    addToCart(item, redirect);
    if (redirect) navigate("/checkout");
    else {
      const cartOffcanvas = document.getElementById("cartmini");
      if (cartOffcanvas && window.bootstrap?.Offcanvas) {
        window.bootstrap.Offcanvas.getOrCreateInstance(cartOffcanvas).show();
      }
    }
  };

  const renderFilters = () => (
    <div className="filter-content">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-bold mb-0">Filters</h4>
        {(selectedCategory !== "All" ||
          minPrice > 0 ||
          maxPrice < 2000 ||
          searchTerm) && (
          <Button
            variant="link"
            className="text-decoration-none text-danger p-0 small fw-semibold"
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        )}
      </div>

      <div className="mb-4">
        <label className="form-label fw-semibold">Search</label>
        <div className="position-relative">
          <Form.Control
            type="text"
            placeholder="What are you looking for?"
            className="bg-light border-0 shadow-none py-2 ps-3 rounded-3"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="mb-4">
        <label className="form-label fw-semibold">Categories</label>
        <div className="list-group list-group-flush">
          <button
            className={`list-group-item list-group-item-action border-0 px-3 rounded-3 mb-1 transition-3 ${selectedCategory === "All" ? "bg-danger text-white fw-bold shadow-sm" : "text-muted"}`}
            onClick={() => navigate("/categories")}
          >
            All Categories
          </button>
          {categoriesWithProducts.map((cat) => (
            <button
              key={cat._id}
              className={`list-group-item list-group-item-action border-0 px-3 rounded-3 mb-1 d-flex justify-content-between align-items-center transition-3 ${selectedCategory === cat.name ? "bg-danger text-white fw-bold shadow-sm" : "text-muted"}`}
              onClick={() =>
                navigate(`/categories/${cat.slug || slugify(cat.name)}`)
              }
            >
              {cat.name}
              {selectedCategory === cat.name && (
                <i className="fa-solid fa-circle-check small"></i>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="mb-2">
        <label className="form-label fw-bold text-uppercase small ls-1 mb-3">
          Price Range
        </label>
        <div className="d-flex justify-content-between align-items-center mb-4 px-1">
          <div className="bg-dark text-white rounded-pill px-3 py-1 shadow d-flex align-items-center">
            <span
              className="text-white-50 me-2 d-none d-sm-inline"
              style={{ fontSize: "0.65rem", fontWeight: "800" }}
            >
              FROM
            </span>
            <span className="fw-bold small">₹{minPrice}</span>
          </div>
          <div className="bg-dark text-white rounded-pill px-3 py-1 shadow d-flex align-items-center">
            <span
              className="text-white-50 me-2 d-none d-sm-inline"
              style={{ fontSize: "0.65rem", fontWeight: "800" }}
            >
              TO
            </span>
            <span className="fw-bold small">₹{maxPrice}</span>
          </div>
        </div>

        <div className="px-1 position-relative mb-3" style={{ height: "30px" }}>
          <div className="custom-multi-range-track"></div>
          <div
            className="custom-multi-range-selected"
            style={{
              left: `${(minPrice / 2000) * 100}%`,
              width: `${((maxPrice - minPrice) / 2000) * 100}%`,
            }}
          ></div>
          <input
            type="range"
            min={0}
            max={2000}
            step={50}
            value={minPrice}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val < maxPrice) setMinPrice(val);
            }}
            className="custom-multi-range-input"
          />
          <input
            type="range"
            min={0}
            max={2000}
            step={50}
            value={maxPrice}
            onChange={(e) => {
              const val = Number(e.target.value);
              if (val > minPrice) setMaxPrice(val);
            }}
            className="custom-multi-range-input"
          />
        </div>

        <div className="d-flex justify-content-between mt-2 px-1">
          <span className="text-muted fw-bold" style={{ fontSize: "0.65rem" }}>
            ₹0
          </span>
          <span className="text-muted fw-bold" style={{ fontSize: "0.65rem" }}>
            ₹2000
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <SeoHelmet
        title="Shop Online | A1 Bakery & Sweets"
        description="Browse and order from our complete collection of handcrafted sweets, snacks, and fresh bakery items. Fast delivery across Madurai and Peraiyur."
        keywords="order sweets online, snacks delivery, bakery items, A1 shop"
      />
      <Navbar />
      <header className="header">
        <div className="header-content">
          <div className="breadcrumb__list mb-20">
            <span>
              <Link to="/">
                Home
              </Link>
            </span>
            <span className="dvdr">
              <i className="fa-regular fa-angle-right"></i>
            </span>
            {categorySlug ? (
              <>
                <span>
                  <Link to="/categories">
                    Shop Now
                  </Link>
                </span>
                <span className="dvdr">
                  <i className="fa-regular fa-angle-right"></i>
                </span>
                <span>{selectedCategory}</span>
              </>
            ) : (
              <span>Shop Now</span>
            )}
          </div>
          <h1>Freshly Made. Ready To Order</h1>
          <p>
            Satisfy your sweet tooth, stock up on crunchy snacks, or send a box
            of joy to loved ones.
          </p>
        </div>
      </header>

      <main className="shopnow-container mt-60 mb-60">
        <div className="container">
          <Row>
            {/* Sidebar Filter Box */}
            <Col xxl={3} xl={3} lg={4} className="d-none d-lg-block shopnow-sidebar-col mb-4">
              <div className="card border-0 p-4 shopnow-filter-card">
                {renderFilters()}
              </div>
            </Col>

            {/* Product Listing */}
            <Col xxl={9} xl={9} lg={8}>
              {/* Mobile Filter Toggle */}
              <div className="d-lg-none mb-4 d-flex justify-content-end">
                <Button
                  variant="danger"
                  className="py-2 px-4 rounded-3 d-flex align-items-center gap-2 shadow-sm"
                  onClick={() => setShowMobileFilters(true)}
                >
                  <i className="fa-solid fa-sliders"></i>
                  Filters
                </Button>
              </div>

              {loading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-danger"></div>
                </div>
              ) : (
                <>
                  <Row>
                    {displayedProducts.length > 0 ? (
                      displayedProducts.map((product, index) => (
                        <Col
                          lg={4}
                          md={6}
                          sm={6}
                          key={getItemKey(product)}
                          className="mb-4"
                        >
                          <DynamicDetailedProductCard
                            product={product}
                            originalIndex={index}
                            listSetter={setProducts}
                            onAddToCart={handleAddToCart}
                            onQuantityChange={(key, _, delta) =>
                              handleQuantityChange(key, delta)
                            }
                            onWeightChange={(key, _, weight) =>
                              handleWeightChange(key, weight)
                            }
                          />
                        </Col>
                      ))
                    ) : (
                      <div className="text-center py-3 w-100">
                        <img
                          src="/assets/img/a1-img/dash__elements/emptycart__img.png"
                          alt="Empty"
                          width="200"
                        />
                        <h4 className="mt-4">
                          No products matching your filters
                        </h4>
                      </div>
                    )}
                  </Row>

                  {isMobile && visibleCount < filteredProducts.length && (
                    <div className="text-center mt-30">
                      <button
                        className="bm-hero__cta"
                        onClick={() => setVisibleCount((prev) => prev + 10)}
                      >
                        <span></span> Load More
                      </button>
                    </div>
                  )}
                </>
              )}
            </Col>
          </Row>
        </div>
      </main>

      {/* Mobile Sidebar (Offcanvas) */}
      <Offcanvas
        show={showMobileFilters}
        onHide={() => setShowMobileFilters(false)}
        placement="end"
        className="rounded-start-4"
      >
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title className="fw-bold">Sort & Filter</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-4">{renderFilters()}</Offcanvas.Body>
      </Offcanvas>

      <FloatingWidgets />
      <Footer />
    </>
  );
}
